import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { videoApi } from '@carrent/api'
import { Icon, JitsiRoom } from '@carrent/ui'


/**
 * Гостевая страница звонка: /call/:id?t=<invite>
 *
 * Живёт ДО логина — страховой комиссар или арендодатель открывает
 * ссылку с телефона и не имеет аккаунта. Поэтому:
 *   - публичный срез берётся из /api/video/guest/<id> (без токена);
 *   - пре-экран наш: имя (и пароль, если нужен) спрашиваем сами,
 *     чтобы гость не встретил интерфейс Jitsi раньше времени;
 *   - все конечные состояния (истекла, отозван, лобби) — свои экраны,
 *     а не белый iframe с ошибкой на английском.
 */

const CLOSED = {
  revoked: 'Звонок отозван организатором',
  expired: 'Ссылка истекла',
  ended: 'Звонок завершён',
}

export default function GuestCallPage() {
  const { id } = useParams()
  const [params] = useSearchParams()
  const inviteToken = params.get('t') || ''

  const [view, setView] = useState(null)      // публичный срез
  const [state, setState] = useState('loading') // loading | form | room | closed | missing
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [token, setToken] = useState(null)
  const [error, setError] = useState('')
  const [joining, setJoining] = useState(false)

  useEffect(() => {
    let alive = true
    videoApi
      .guestView(id)
      .then(({ data }) => {
        if (!alive) return
        const v = data.data
        setView(v)
        setState(CLOSED[v.status] ? 'closed' : 'form')
      })
      .catch(() => alive && setState('missing'))
    return () => {
      alive = false
    }
  }, [id])

  const join = async () => {
    setJoining(true)
    setError('')
    try {
      const payload = { name: name.trim() }
      if (inviteToken) payload.invite_token = inviteToken
      if (view?.need_password) payload.password = password
      const { data } = await videoApi.guestToken(id, payload)
      setToken(data.data)
      setState('room')
    } catch (e) {
      const status = e?.response?.status
      if (status === 410) setState('closed')
      else setError(e.response?.data?.message || 'Не удалось подключиться')
    } finally {
      setJoining(false)
    }
  }

  if (state === 'loading') {
    return (
      <div className="guest-call state">
        <span className="spinner" />
      </div>
    )
  }

  if (state === 'missing') {
    return (
      <div className="guest-call state">
        <Icon name="warning" size={32} />
        <p className="state__title">Звонок не найден</p>
        <p className="state__text">Проверьте ссылку — возможно, она неполная.</p>
      </div>
    )
  }

  if (state === 'closed') {
    return (
      <div className="guest-call state">
        <Icon name="warning" size={32} />
        <p className="state__title">{CLOSED[view?.status] || 'Звонок недоступен'}</p>
        <p className="state__text">Запросите новую ссылку у организатора.</p>
      </div>
    )
  }

  if (state === 'room' && token) {
    return (
      <div className="video-guest-room">
        <JitsiRoom
          token={token}
          subject={view?.title}
          roomConfig={view?.room || {}}
          guest
          onClose={() => setState('closed')}
        />
      </div>
    )
  }

  // Пре-экран
  return (
    <div className="guest-call video-guest">
      <div className="card flex-col gap">
        <h1 className="section-title video-guest-title">
          {view?.title}
        </h1>
        <p className="text-muted video-subtitle video-hint--0">
          Видеозвонок. Браузер спросит доступ к камере и микрофону.
        </p>
        {view?.description && (
          <p className="video-call-desc">{view.description}</p>
        )}

        {!inviteToken && (
          <div className="field">
            <label className="field__label" htmlFor="guest-name">Ваше имя</label>
            <input
              id="guest-name"
              className="input"
              value={name}
              maxLength={100}
              onChange={(e) => setName(e.target.value)}
              placeholder="Как вас представить участникам"
            />
          </div>
        )}

        {view?.need_password && (
          <div className="field">
            <label className="field__label" htmlFor="guest-password">Пароль звонка</label>
            <input
              id="guest-password"
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && join()}
            />
          </div>
        )}

        {view?.need_invite && !inviteToken && (
          <div className="alert alert--error">
            Для этого звонка нужна персональная ссылка-приглашение.
          </div>
        )}

        {error && <div className="alert alert--error">{error}</div>}

        <button
          type="button"
          className="btn btn--primary btn--full"
          onClick={join}
          disabled={
            joining
            || (!inviteToken && !name.trim())
            || (view?.need_invite && !inviteToken)
          }
        >
          {joining ? <span className="spinner spinner--sm" /> : null}
          Подключиться
        </button>
      </div>
    </div>
  )
}
